import React, {
  Suspense,
  useEffect,
  useState,
  useCallback,
  useContext,
} from 'react';
import {
  Routes as Router,
  Route,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  getScreenResolution,
  setCustomPageName,
  setCustomUserData,
} from '../utils/AppDUtil';
import { LoginCallback, useOktaAuth } from '@okta/okta-react';
import Dashboard from '../containers/Dashboard/Dashboard';
import { useLayoutNavigation } from '../hooks/layout-navigation-hook';
import OnboardingPage from '../containers/OnboardingPage/OnboardingPage';
import { constants } from '../constants/Constants';
import { helperFunctions } from '../utils/helperFunctions';
const PowerBIDashboard = React.lazy(
  () => import('containers/PowerBI/PowerBIDashboard')
);
import { FilterContext } from '../components/FilterBar/FilterProvider';
import { AppProvider } from '../context/AppContext';
import KpiDefinitionDrawer from '../components/redesign/kpiDefinitionDrawer';
import MfeTestPage from '../v2/components/MfeTestPage';
import AskPsdPageV2 from '../components/AskPsd/v2/AskPsdPageV2';

// Import Redux hooks and auth slice actions
import { useDispatch, useSelector } from 'react-redux';
import { setCredentials, clearCredentials } from '../store/slices/authSlice';
import PrivateRoutes from './PrivateRoutes';
import { ThemeContext } from '../context/ThemeContext';

// Custom LoginCallback with PKCE error handling
const CustomLoginCallback = () => {
  const handleError = (error) => {
    console.warn(
      'Auth error during login callback, redirecting to fresh login:',
      error?.message
    );
    sessionStorage.removeItem('okta-pkce-storage');
    localStorage.removeItem('okta-pkce-storage');
    window.location.replace('/');
    return null;
  };

  return <LoginCallback errorComponent={({ error }) => handleError(error)} />;
};

const Routes = () => {
  const { setMode } = useContext(ThemeContext);
  const {
    betaFeatureToggle,
    setSelectedProgram,
    setSelectedProduct,
    productValues,
    regionValues,
    selectedProgram,
    selectedProduct,
  } = useContext(FilterContext);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const oktaContext = useOktaAuth();
  const authState = oktaContext?.authState;
  const oktaAuth = oktaContext?.oktaAuth;
  const [idleTimer, setIdleTimer] = useState(null);
  const { getUserRoles } = useLayoutNavigation();
  const { debounce } = helperFunctions();
  const dispatch = useDispatch();
  const [pendingProductId, setPendingProductId] = useState(null);
  const [programSet, setProgramSet] = useState(false);

  // Get isMFE and mfeHostConfig directly from Redux store with useSelector
  const isMFE = useSelector((state) => state.app.isMFE);
  const mfeHostConfig = useSelector((state) => state.app.mfeHostConfig);

  const MFE_PRODUCT_MAP = {
    b2b_steerco: constants.products.PepsiConnect,
    mfg_mct: constants.products.ManufacturingControlTower,
    ctx_profit_to_serve: constants.products.MCVO,
  };

  const setMfeProductConfig = (config) => {
    if (!config?.type || !productValues?.length) {
      return;
    }

    const productId = MFE_PRODUCT_MAP[config.type];
    if (!productId) {
      return;
    }

    const targetProduct = productValues?.find(
      (prod) => prod.productId === productId
    );

    if (!targetProduct) {
      console.warn(
        `[MFE Config] Product not found for MFE type: ${config?.type}`
      );
      return;
    }

    const programObj = regionValues.find(
      (program) => program.programId === targetProduct.programId
    );

    setSelectedProgram(programObj);
    setSelectedProduct(targetProduct);
  };

  useEffect(() => {
    if (!isMFE || !mfeHostConfig || !productValues?.length) {
      return;
    }
    // CTX MFE allows switching between ProfitToServe and MCVO. Don't override
    // when user has selected either; preserves user-initiated product change.
    if (
      mfeHostConfig?.type === constants?.ctxMfeHostConfig &&
      (selectedProduct?.productId === constants.products.ProfitToServe ||
        selectedProduct?.productId === constants.products.MCVO)
    ) {
      return;
    }
    setMfeProductConfig(mfeHostConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMFE, mfeHostConfig, productValues, selectedProduct]);
  // Check if a product ID in the URL is valid
  const isValidProductId = useCallback((productId) => {
    // Convert to number for comparison
    const numProductId = Number(productId);

    // Check if the productId exists in constants.products values
    return Object.values(constants.products).includes(numProductId);
  }, []);

  // Reset the programSet flag when pending product ID changes
  useEffect(() => {
    if (pendingProductId) {
      setProgramSet(false);
    }
  }, [pendingProductId]);

  // Handle extracting product ID from URL - only for top-level route
  useEffect(() => {
    // Only apply deeplinking on the top-level route
    if (pathname === '/') {
      const productId = searchParams.get('productId');

      if (productId && isValidProductId(productId)) {
        const numProductId = Number(productId);
        // Store the product ID to be set after program is set
        setPendingProductId(numProductId);
      }
    }
  }, [pathname, searchParams, isValidProductId]);

  // Set program based on pending product ID
  useEffect(() => {
    if (
      pendingProductId &&
      productValues &&
      productValues.length > 0 &&
      regionValues &&
      regionValues.length > 0
    ) {
      // Find product by ID to get its programId
      const productObj = productValues.find(
        (product) => product.productId === pendingProductId
      );

      if (productObj && productObj.programId) {
        // Find the program object
        const programObj = regionValues.find(
          (program) => program.programId === productObj.programId
        );

        if (programObj) {
          // Use setTimeout to allow the UI to update
          setTimeout(() => {
            // Set the program first
            setSelectedProgram(programObj);
            // Mark program as set
            setProgramSet(true);
          }, 0);
        }
      }
    }
  }, [pendingProductId, productValues, regionValues, setSelectedProgram]);

  // Set the product after program has been set
  useEffect(() => {
    if (
      pendingProductId &&
      programSet &&
      selectedProgram &&
      productValues &&
      productValues.length > 0
    ) {
      // Find products that belong to the selected program
      const availableProducts = productValues.filter(
        (product) => product.programId === selectedProgram.programId
      );

      // Find the specific product with our pending product ID
      const targetProduct = availableProducts.find(
        (product) => product.productId === pendingProductId
      );

      if (targetProduct) {
        // Set the product after a delay to allow UI to update properly
        setTimeout(() => {
          setSelectedProduct(targetProduct);
          // Clear pending product ID
          setPendingProductId(null);
          // Reset programSet flag
          setProgramSet(false);
        }, 100);
      }
    }
  }, [
    pendingProductId,
    programSet,
    selectedProgram,
    productValues,
    setSelectedProduct,
  ]);

  // Theme effect
  useEffect(() => {
    if (!isMFE) {
      return;
    }

    // set initial theme mode when mfeHostConfig changes
    if (mfeHostConfig?.themeMode) {
      setMode(mfeHostConfig.themeMode);
    }

    const handleThemeChange = (event) => {
      if (event.detail) {
        setMode(event.detail);
      }
    };

    // Listen for the custom event
    window.addEventListener('themeModeChanged', handleThemeChange);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('themeModeChanged', handleThemeChange);
    };
  }, [isMFE, mfeHostConfig, setMode]); // Include all dependencies

  // Redirect if running as MFE and redirectUrl is set in mfeHostConfig
  useEffect(() => {
    if (isMFE && mfeHostConfig?.redirectUrl) {
      navigate(mfeHostConfig.redirectUrl, { replace: true });
    }
  }, [isMFE, mfeHostConfig, navigate]);

  // This function checks the Okta token and updates Redux state accordingly.
  const getOktaStorageToken = () => {
    oktaAuth?.tokenManager.get('accessToken').then((accessToken) => {
      const accessTokenExpiresAt = accessToken?.expiresAt;
      const epochTime = Math.floor(new Date().getTime() / 1000);
      if (accessTokenExpiresAt && accessTokenExpiresAt <= epochTime) {
        localStorage.clear();
        dispatch(clearCredentials());
        oktaAuth.signOut();
        window.location.href = '/';
      } else {
        getUserRoles();
        if (accessToken && accessToken.claims) {
          dispatch(
            setCredentials({
              user: accessToken.claims,
              token: accessToken.accessToken,
            })
          );
          return accessToken.claims;
        }
      }
    });
  };

  const debouncedHandleIdle = useCallback(
    debounce(() => {
      // Record the timestamp when the user is idle
      setIdleTimer(Date.now());
    }, 1000),
    [debounce]
  );

  useEffect(() => {
    const checkIdleAndToken = () => {
      oktaAuth?.tokenManager.get('accessToken').then((accessToken) => {
        const accessTokenExpiresAt = accessToken?.expiresAt;
        const epochTime = Math.floor(new Date().getTime() / 1000);
        if (accessTokenExpiresAt && accessTokenExpiresAt <= epochTime) {
          localStorage.clear();
          dispatch(clearCredentials());
          oktaAuth.signOut();
          window.location.href = '/';
        } else if (idleTimer && authState.isAuthenticated) {
          const idleTime = Date.now() - idleTimer;
          if (idleTime >= constants?.IDLE_TIME) {
            localStorage.clear();
            dispatch(clearCredentials());
            oktaAuth.signOut();
            window.location.href = '/';
          }
        }
      });
    };

    // Set up event listeners for user activity
    document.addEventListener('click', debouncedHandleIdle);
    document.addEventListener('wheel', debouncedHandleIdle);
    document.addEventListener('keydown', debouncedHandleIdle);

    // Check idle status and token expiry every minute
    const timerInterval = setInterval(checkIdleAndToken, 60000);

    return () => {
      document.removeEventListener('click', debouncedHandleIdle);
      document.removeEventListener('wheel', debouncedHandleIdle);
      document.removeEventListener('keydown', debouncedHandleIdle);
      clearInterval(timerInterval);
    };
  }, [idleTimer, authState, oktaAuth, debouncedHandleIdle, dispatch]);

  useEffect(() => {
    const oktaTokenStorageData = getOktaStorageToken();
    setCustomUserData({
      gpid: oktaTokenStorageData?.gpid || '',
      email: oktaTokenStorageData?.email || '',
      FirstName: oktaTokenStorageData?.FirstName || '',
      LastName: oktaTokenStorageData?.LastName || '',
      screenResolution: getScreenResolution(),
      language: navigator?.language,
    });

    setCustomPageName(pathname);
  }, [pathname, authState, navigate]);

  // Redirect from /psdlite if betaFeatureToggle is disabled
  useEffect(() => {
    if (!betaFeatureToggle && pathname === '/psdlite') {
      navigate('/dashboard');
    }
  }, [betaFeatureToggle, pathname, navigate]);

  const renderPage = (Component) => {
    return (
      <AppProvider>
        {/* <LayoutV2> */}
        <Component />
        <KpiDefinitionDrawer />
        {/* </LayoutV2> */}
      </AppProvider>
    );
  };

  const renderDashboardPage = () => renderPage(Dashboard);

  const renderBetaEnabledComponent = (Component) => {
    if (betaFeatureToggle) {
      return renderPage(Component);
    } else {
      return null;
    }
  };

  return (
    <Suspense>
      <Router>
        <Route element={<PrivateRoutes />}>
          <Route
            path="/dashboard"
            title="Dashboard"
            element={renderDashboardPage()}
          />
          <Route
            path="/psd"
            title="Dashboard"
            element={renderDashboardPage()}
          />
          <Route
            path="/psdlite"
            title="PSD Lite Dashboard"
            element={renderBetaEnabledComponent(PowerBIDashboard)}
          />
          <Route
            path="/"
            title="ExecutiveOverView"
            exact={true}
            element={renderDashboardPage()}
          />

          <Route
            path="/mfe-test"
            title="Mfe Test"
            exact={true}
            element={<MfeTestPage />}
          />

          <Route path="/ask-psd" title="Ask PSD" element={<AskPsdPageV2 />} />

          <Route
            path="/logout"
            title="Logout"
            element={<OnboardingPage showLogout={true} />}
          />
        </Route>

        <Route path="/login" title="Login" element={<CustomLoginCallback />} />
      </Router>
    </Suspense>
  );
};

export default Routes;
